import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FilterBar } from '@opendate-app/setlist';
import ReportingPageLayout from './ReportingPageLayout';
import useReportingPage from '../hooks/useReportingPage';
import { formatCell } from '../constants/eventPnlColumnsConfig';
import { STATUS_OPTIONS } from '../constants/eventsFiltersConfig';
import '../styles/reportingStyles.css';

const axios = require('axios').default;
const qs = require('qs');

const noop = () => {};

const formatFilterValue = (filter, values) => {
  if (values?.length > 1) return `${values.length} selected`;
  if (values?.length === 1) return filter.options.find((option) => option.value === values[0])?.label;
  if (filter.key === 'calendar_classification') return 'All but canceled';
  return 'Any';
};

function ReportingFinancesEventPnlPage({ team, venueOwnerships }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    activeFilters, setActiveFilters,
  } = useReportingPage('month_to_date');

  const [columns, setColumns] = useState({ ticketing: [], income: [], expenses: [] });
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filterDefinitions = useMemo(() => {
    const definitions = [];
    if (venueOwnerships?.length > 0) {
      definitions.push({
        key: 'venue_ownership_id',
        label: 'Venue',
        options: venueOwnerships.map((venueOwnership) => ({
          value: String(venueOwnership.id),
          label: venueOwnership.name || venueOwnership.venue_name || `Venue ${venueOwnership.id}`,
        })),
      });
    }
    definitions.push({ key: 'calendar_classification', label: 'Status', options: STATUS_OPTIONS });
    return definitions;
  }, [venueOwnerships]);

  const apiParams = useMemo(() => ({
    start_date: dateRange?.start,
    end_date: dateRange?.end,
    search: search || undefined,
    venue_ownership_id: activeFilters.venue_ownership_id?.length ? activeFilters.venue_ownership_id : undefined,
    calendar_classification: activeFilters.calendar_classification?.length ? activeFilters.calendar_classification : undefined,
  }), [dateRange, search, activeFilters]);

  useEffect(() => {
    if (!team?.id) return undefined;
    let ignore = false;
    setLoading(true);
    setError(null);
    axios.get(`/teams/${team.id}/reporting/finances/event-pnl/data`, {
      params: apiParams,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
    })
      .then((response) => {
        if (ignore) return;
        setColumns(response.data.columns || { ticketing: [], income: [], expenses: [] });
        setRows(response.data.rows || []);
        setGrandTotal(response.data.grand_total || null);
      })
      .catch((requestError) => {
        if (ignore) return;
        console.error('Failed to load Event P&L report', requestError);
        setError('Failed to load the Event P&L report.');
      })
      .finally(() => {
        if (ignore) return;
        setLoading(false);
      });
    return () => { ignore = true; };
  }, [team, apiParams]);

  const exportUrl = useCallback((format) => {
    const query = qs.stringify({ ...apiParams, format }, { arrayFormat: 'brackets' });
    return `/teams/${team?.id}/reporting/finances/event-pnl/export?${query}`;
  }, [team, apiParams]);

  const ticketingCols = columns.ticketing || [];
  const incomeCols = columns.income || [];
  const expenseCols = columns.expenses || [];

  const DATE_COL_WIDTH = 104;
  const EVENT_COL_WIDTH = 200;
  const STICKY_BG = {
    head: 'var(--sl-bg-tertiary, #f2f4f7)',
    body: 'var(--sl-bg-primary, #fff)',
    foot: 'var(--sl-bg-tertiary, #f2f4f7)',
  };

  const stickyCol = (colIndex, variant) => {
    if (colIndex > 1) return undefined;
    const isEvent = colIndex === 1;
    const width = isEvent ? EVENT_COL_WIDTH : DATE_COL_WIDTH;
    return {
      position: 'sticky',
      left: isEvent ? DATE_COL_WIDTH : 0,
      width,
      minWidth: width,
      maxWidth: width,
      background: STICKY_BG[variant],
      zIndex: variant === 'body' ? 1 : 4,
      boxShadow: isEvent ? '1px 0 0 var(--sl-border-secondary, #e4e7ec)' : undefined,
    };
  };

  return (
    <ReportingPageLayout
      team={team}
      activeKey="finances_event_pnl"
      title="Event P&L"
      subtitle="Profit and loss breakdown by event. Canceled events are excluded unless you select Canceled in the Status filter."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      showFilters={false}
      activeFilters={{}}
      filterGroups={[]}
      filterSidebarOpen={false}
      setFilterSidebarOpen={noop}
      handleFilterChange={noop}
      handleRemoveFilter={noop}
      handleClearAllFilters={noop}
      toolbarExtras={(
        <>
          <div style={{ position: 'relative', width: 240 }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--sl-text-disabled, #98a2b3)', fontSize: 14 }} />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search events..."
              aria-label="Search events"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ paddingLeft: 32, border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, height: 32 }}
            />
          </div>
          <a className="btn btn-sm" href={exportUrl('csv')} style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}>
            Export CSV
          </a>
          <a className="btn btn-sm" href={exportUrl('xlsx')} style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}>
            Export XLSX
          </a>
          <div className="w-100">
            <FilterBar
              variant="plain"
              filters={filterDefinitions}
              alwaysShow={['calendar_classification']}
              values={activeFilters}
              onChange={setActiveFilters}
              formatValue={formatFilterValue}
            />
          </div>
        </>
      )}
    >
      {error && <div className="alert alert-danger">{error}</div>}

      {loading && <div className="text-muted p-4">Loading&hellip;</div>}

      {!loading && !error && rows.length === 0 && (
        <div
          className="text-muted p-4 text-center"
          style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8 }}
        >
          No events with financial activity in this range.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8 }}>
          <div className="table-responsive event-pnl-table-scroll" style={{ maxHeight: '70vh', overflow: 'auto' }}>
            <table className="table table-sm table-bordered mb-0">
              <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 3, background: 'var(--sl-bg-tertiary, #f2f4f7)' }}>
                <tr>
                  {ticketingCols.map((c, idx) => <th key={c.key} style={stickyCol(idx, 'head')}>{c.label}</th>)}
                  {incomeCols.map((c) => <th key={`inc-${c}`}>{c}</th>)}
                  {expenseCols.map((c) => <th key={`exp-${c}`}>{c}</th>)}
                  <th>Total Income</th>
                  <th>Total Expenses</th>
                  <th>Net Profit (Loss)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.event ? `${row.event}-${row.date}-${i}` : i}>
                    {ticketingCols.map((c, idx) => <td key={c.key} style={stickyCol(idx, 'body')}>{formatCell(c.type, row[c.key])}</td>)}
                    {incomeCols.map((c) => <td key={`inc-${c}`}>{formatCell('currency', (row.income || {})[c])}</td>)}
                    {expenseCols.map((c) => <td key={`exp-${c}`}>{formatCell('currency', (row.expenses || {})[c])}</td>)}
                    <td>{formatCell('currency', row.total_income)}</td>
                    <td>{formatCell('currency', row.total_expenses)}</td>
                    <td>{formatCell('currency', row.net)}</td>
                  </tr>
                ))}
              </tbody>
              {grandTotal && (
                <tfoot>
                  <tr className="font-weight-bold" style={{ position: 'sticky', bottom: 0, zIndex: 3, background: 'var(--sl-bg-tertiary, #f2f4f7)' }}>
                    <td style={stickyCol(0, 'foot')}>Grand Total</td>
                    {ticketingCols.slice(1).map((c, i) => (
                      <td key={c.key} style={stickyCol(i + 1, 'foot')}>
                        {['capacity', 'tickets_sold', 'checked_in'].includes(c.key) ? formatCell('integer', grandTotal[c.key]) : ''}
                      </td>
                    ))}
                    {incomeCols.map((c) => <td key={`inc-${c}`}>{formatCell('currency', (grandTotal.income || {})[c])}</td>)}
                    {expenseCols.map((c) => <td key={`exp-${c}`}>{formatCell('currency', (grandTotal.expenses || {})[c])}</td>)}
                    <td>{formatCell('currency', grandTotal.total_income)}</td>
                    <td>{formatCell('currency', grandTotal.total_expenses)}</td>
                    <td>{formatCell('currency', grandTotal.net)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </ReportingPageLayout>
  );
}

export default ReportingFinancesEventPnlPage;
